import React from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Crown, MessageCircle, Calendar, Upload, Palette, Sparkles, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import PlusComparisonModal from './PlusComparisonModal';
import MsStoreIAPModal from '../Common/MsStoreIAPModal';

const PlusPromoModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation('shared');
  const { isPlusUser } = useAuth();
  const [isWindowsStore, setIsWindowsStore] = React.useState(false);
  const [isComparisonModalOpen, setIsComparisonModalOpen] = React.useState(false);
  const [showStoreIap, setShowStoreIap] = React.useState(false);

  React.useEffect(() => {
    if (typeof window !== 'undefined' && window.api?.isWindowsStore !== undefined) {
      setIsWindowsStore(window.api.isWindowsStore);
    }
  }, []);

  React.useEffect(() => {
    if (!isOpen) setShowStoreIap(false);
  }, [isOpen]);

  // Plus kullanıcısı ise modal'ı gösterme
  if (isPlusUser) {
    return null;
  }

  const handleStorePurchase = () => {
    setShowStoreIap(true);
  };

  const handleShowComparison = () => {
    setIsComparisonModalOpen(true);
  };

  const plusFeatures = [
    {
      icon: MessageCircle,
      title: t('plusPromoModal.features.chat.title'),
      description: t('plusPromoModal.features.chat.description'),
    },
    {
      icon: Calendar,
      title: t('plusPromoModal.features.calendar.title'),
      description: t('plusPromoModal.features.calendar.description'),
    },
    {
      icon: Upload,
      title: t('plusPromoModal.features.upload.title'),
      description: t('plusPromoModal.features.upload.description'),
    },
    {
      icon: Palette,
      title: t('plusPromoModal.features.themes.title'),
      description: t('plusPromoModal.features.themes.description'),
    },
  ];

  if (!isOpen) return null;

  const modalContent = (
    <AnimatePresence>
      {isOpen && !showStoreIap && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[99999]"
          />

          <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-[100000] w-full max-w-2xl p-4 pointer-events-none">
            <motion.div
              initial={{ opacity: 0, y: -50, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -50, scale: 0.9 }}
              transition={{
                type: 'spring',
                stiffness: 300,
                damping: 30,
              }}
              className="relative w-full bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-3xl shadow-2xl border border-amber-500/30 overflow-hidden pointer-events-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-amber-500/10 to-transparent opacity-50" />
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-amber-500 to-transparent" />

              <button
                onClick={onClose}
                className="absolute top-4 right-4 z-10 p-2 rounded-xl bg-gray-800/80 hover:bg-gray-700/80 text-[var(--color-text-muted)] hover:text-white transition-all duration-200 border border-gray-700/50 hover:border-amber-500/50"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="relative p-8 md:p-12">
                <div className="text-center mb-8">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                    className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-amber-500 via-yellow-500 to-amber-600 mb-4 shadow-lg shadow-amber-500/50"
                  >
                    <Crown className="w-10 h-10 text-white" strokeWidth={2} />
                  </motion.div>
                  <motion.h2
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="text-4xl font-bold bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-500 bg-clip-text text-transparent mb-3"
                  >
                    {t('plusPromoModal.title')}
                  </motion.h2>
                  <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="text-[var(--color-text-muted)] text-lg"
                  >
                    {t('plusPromoModal.subtitle')}
                  </motion.p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                  {plusFeatures.map((feature, index) => {
                    const Icon = feature.icon;
                    return (
                      <motion.div
                        key={feature.title}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.5 + index * 0.1 }}
                        className="flex items-start space-x-4 p-4 rounded-xl bg-gray-800/50 border border-gray-700/50 hover:border-amber-500/50 transition-all duration-200"
                      >
                        <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-gradient-to-br from-amber-500/20 to-yellow-500/20 border border-amber-500/30 flex items-center justify-center">
                          <Icon className="w-6 h-6 text-amber-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-white font-semibold mb-1 whitespace-nowrap text-base" style={{ fontSize: 'clamp(0.875rem, 2vw, 1rem)' }}>{feature.title}</h3>
                          <p className="text-[var(--color-text-muted)] text-xs leading-tight" style={{ maxHeight: '2.5rem', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', wordBreak: 'break-word' }}>{feature.description}</p>
                        </div>
                        <Check className="w-5 h-5 text-amber-400 flex-shrink-0 mt-1" />
                      </motion.div>
                    );
                  })}
                </div>

                <div className="flex flex-col gap-4 mb-4">
                  {isWindowsStore ? (
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={handleStorePurchase}
                      className="w-full relative overflow-hidden bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-600 hover:from-amber-600 hover:via-yellow-600 hover:to-amber-700 text-white font-bold py-4 px-6 rounded-xl shadow-lg shadow-amber-500/50 hover:shadow-amber-500/70 transition-all duration-200 flex items-center justify-center space-x-2 group"
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
                      <Sparkles className="w-5 h-5 z-10" />
                      <span className="z-10">{t('plusPromoModal.storeButton')}</span>
                      <Crown className="w-5 h-5 z-10" />
                    </motion.button>
                  ) : (
                    <p className="text-center text-sm text-[var(--color-text-muted)] leading-relaxed px-2">
                      {t('plusPromoModal.storeOnlyAvailability')}
                    </p>
                  )}
                </div>

                <div className="text-center">
                  <button
                    onClick={handleShowComparison}
                    className="text-amber-400 hover:text-amber-300 font-medium text-sm transition-colors duration-200 underline decoration-amber-500/50 hover:decoration-amber-400/70 underline-offset-4"
                  >
                    {t('plusPromoModal.seeMoreFeatures')}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );

  if (typeof document !== 'undefined') {
    return (
      <>
        {createPortal(modalContent, document.body)}
        <PlusComparisonModal isOpen={isComparisonModalOpen} onClose={() => setIsComparisonModalOpen(false)} />
        {isWindowsStore && (
          <MsStoreIAPModal
            isOpen={showStoreIap}
            onClose={() => {
              setShowStoreIap(false);
              onClose();
            }}
          />
        )}
      </>
    );
  }

  return (
    <>
      {modalContent}
      <PlusComparisonModal isOpen={isComparisonModalOpen} onClose={() => setIsComparisonModalOpen(false)} />
    </>
  );
};

export default PlusPromoModal;
